import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  getDrivers,
  suspendDriver,
  activateDriver,
  deleteDriver,
  unclaimDriver,
  getDriverDetails,
  updateDriver,
  getSignedDriverDocUrl,
  approveDriver
} from "../services/driverService";
import { StyledPaper } from "../assets/styles/tableStyle"
import FancyContextMenu from "../components/FancyContextMenu";
import {DriverStatus} from "../types/enums";
import { useConfirm } from "../components/feedback/ConfirmDialog";
import { useToast } from "../lib/useToast";
import { getApiErrorMessage } from "../lib/toast";
import { useCan } from "../auth/Can";
import { useDebounce } from "../hooks/useDebounce";
import { useCursorList, toCursorPage } from "../hooks/useCursorList";
import LoadMoreBar from "../components/common/LoadMoreBar";
import {
  Driver,
  DocStatus,
  VerifiedForm,
  allFields,
  emptyVerifiedForm,
} from "./drivers/driverTypes";
import { buildDriverActions } from "./drivers/driverMenuActions";
import DriverApproveModal from "./drivers/DriverApproveModal";
import DriverComplianceModal from "./drivers/DriverComplianceModal";
import DriverQuickViewModal from "./drivers/DriverQuickViewModal";
import DriverTable from "./drivers/DriverTable";
import DriverFiltersToolbar from "./drivers/DriverFiltersToolbar";
import { useAppVersionOptions } from "../hooks/useAppVersionOptions";

// Re-export the driver types so existing importers (and tests) keep their paths.
export type { Driver };
export { DocStatus };

const DriverPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const { can } = useCan();
  const canWriteDrivers = can('drivers:write');
  const canApproveDrivers = can('drivers:approve');

  // `loading` is kept for mutation spinners; table loading comes from the hook.
  const [loading, setLoading] = useState(false);

  // Filters State
  const [searchTerm, setSearchTerm] = useState<string>(searchParams.get('search') || "");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [availabilityFilter, setAvailabilityFilter] = useState<string>("all");
  const [approvalFilter, setApprovalFilter] = useState<string>("all"); // all | ready | approved | incomplete
  // all | unknown (old app) | eligible (>= live location_ping gate) | <exact versionCode>
  // Seeded from the URL so the App Adoption panel can deep-link straight into a cohort
  // (e.g. /drivers?appVersion=unknown for every old-app driver).
  const [appVersionFilter, setAppVersionFilter] = useState<string>(searchParams.get('appVersion') || "all");
  // Distinct versions + the live gate, fetched once. Degrades to the static buckets on 403/network.
  const appVersionOptions = useAppVersionOptions('driver');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('');

  // Server-side, cursor-paginated, debounced search (scales to millions).
  const debouncedSearch = useDebounce(searchTerm);
  const driversList = useCursorList<Driver>({
    key: ['drivers', { search: debouncedSearch, status: statusFilter, availability: availabilityFilter, approval: approvalFilter, appVersion: appVersionFilter }],
    pageSize: 25,
    fetchPage: async ({ cursor, limit }) => {
      const res = await getDrivers({
        cursor,
        limit,
        search: debouncedSearch || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        availability:
          availabilityFilter === 'available' ? true : availabilityFilter === 'unavailable' ? false : undefined,
        approval: approvalFilter !== 'all' ? approvalFilter : undefined,
        appVersion: appVersionFilter !== 'all' ? appVersionFilter : undefined,
      });
      return toCursorPage<Driver>(res.data);
    },
  });

  // Modal State
  const [detailOpen, setDetailOpen] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [editData, setEditData] = useState<Partial<Driver>>({});
  const [editLoading, setEditLoading] = useState<boolean>(false);
  const [menu, setMenu] = useState<{ top: number; left: number } | null>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  // Two separate modals
  const [verifiedModalOpen, setVerifiedModalOpen] = useState(false);
  const [complianceModalOpen, setComplianceModalOpen] = useState(false);

  const [verifiedForm, setVerifiedForm] = useState<VerifiedForm>(emptyVerifiedForm);

  const handleContextMenu = (event: React.MouseEvent, row: any) => {
    event.preventDefault();
    setSelectedRow(row);
    setMenu({ top: event.clientY, left: event.clientX });
  };
  const handleCloseMenu = () => setMenu(null);

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const actions = buildDriverActions({
    selectedRow,
    canWriteDrivers,
    canApproveDrivers,
    onViewDetails: (id) => navigate(`/driver/${id}`),
    onEdit: (id) => handleRowClick(id),
    onSuspend: (id) => handleSuspend(id),
    onActivate: (id) => handleActivate(id),
    onApprove: (driver, type) => handleOpenDriverVerified(driver, type),
    onShowRides: (row) => handleShowRides(row),
    onShowWallet: (row) => handleShowWalletDetails(row),
    onUnclaim: (driver) => handleUnclaim(driver),
    onDelete: (id) => handleDelete(id),
  });

  // Sync the search term into the URL (without re-triggering fetches).
  useEffect(() => {
    const currentParams = new URLSearchParams(searchParams);
    if (debouncedSearch) currentParams.set('search', debouncedSearch);
    else currentParams.delete('search');
    setSearchParams(currentParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const refreshDrivers = () => driversList.refetch();

  const handleRowClick = async (id: number) => {
    setSelectedId(id);
    setDetailOpen(true);
    setEditLoading(true);
    try {
      const res = await getDriverDetails(id);
      setSelectedDriver(res.data);
      setEditData(res.data);
    } finally {
      setEditLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedId(null);
    setSelectedDriver(null);
    setEditData({});
  };

  const handleFieldChange = (field: keyof Driver, value: string | boolean | DriverStatus) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveEdit = async () => {
    if (!selectedId) return;
    setEditLoading(true);
    try {
      const updateFields: any = {};
      const editableFields = allFields.filter(f => f !== 'status');
      editableFields.forEach((field) => {
        if (editData[field] !== undefined) updateFields[field] = editData[field];
      });
      await updateDriver(selectedId, updateFields);
      toast.success("Driver updated");
      refreshDrivers();
      setDetailOpen(false);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to update driver"));
    } finally {
      setEditLoading(false);
    }
  };

  const handleOpenDriverVerified = async (driver: Driver, type: 'MANDATORY' | 'COMPLIANCE') => {
    // --- Fetch fresh details to ensure deep arrays exist ---
    let fullDriver = driver;
    try {
      const res = await getDriverDetails(driver.id);
      if(res.data) fullDriver = res.data;
    } catch(e) {
      console.error("Could not fetch latest details, using list data");
    }

    const toArray = (val: string | string[] | undefined) => {
      if (!val) return [];
      return Array.isArray(val) ? val : [val];
    };

    // Prepare arrays for signing
    const cKeys = toArray(fullDriver.cImageUrl);
    const dlKeys = toArray(fullDriver.dlImageUrl);
    const pKeys = toArray(fullDriver.profilePhotoUrl);
    const insKeys = toArray(fullDriver.insuranceUrls);
    const pucKeys = toArray(fullDriver.pucUrls);
    const permKeys = toArray(fullDriver.statePermitUrls);
    const aitpKeys = toArray(fullDriver.aitpUrls);

    // Fetch all signed URLs in parallel
    const [cUrls, dlUrls, profileUrls, insUrls, pucUrls, permUrls, aitpUrls] = await Promise.all([
      Promise.all(cKeys.map(k => getSignedDriverDocUrl(k))),
      Promise.all(dlKeys.map(k => getSignedDriverDocUrl(k))),
      Promise.all(pKeys.map(k => getSignedDriverDocUrl(k))),
      Promise.all(insKeys.map(k => getSignedDriverDocUrl(k))),
      Promise.all(pucKeys.map(k => getSignedDriverDocUrl(k))),
      Promise.all(permKeys.map(k => getSignedDriverDocUrl(k))),
      Promise.all(aitpKeys.map(k => getSignedDriverDocUrl(k))),
    ]);

    setVerifiedForm({
      id: fullDriver.id,
      licenseNumber: fullDriver.licenseNumber || "",
      vehicleLicensePlateNumber: fullDriver.vehicleLicensePlateNumber || "",

      // Mandatory Booleans
      isProfilePhotoVerified: fullDriver.isProfilePhotoVerified,
      isAvailable: fullDriver.isAvailable,

      // Extra Available Fields
      isDLAvailable: fullDriver.isDLAvailable || false,
      isRCAvailable: fullDriver.isRCAvailable || false,
      isProfilePhotoAvailable: fullDriver.isProfilePhotoAvailable || false,
      isTermsAndConditionAccepted: fullDriver.isTermsAndConditionAccepted || false,
      mandatoryPermissions: fullDriver.mandatoryPermissions || {},

      // Mandatory Enum Statuses (Fallback to Verified/Missing if null)
      rcStatus: fullDriver.rcStatus || (fullDriver.isRCVerified ? DocStatus.VERIFIED : DocStatus.MISSING),
      dlStatus: fullDriver.dlStatus || (fullDriver.isDLVerified ? DocStatus.VERIFIED : DocStatus.MISSING),

      cImageUrl: cUrls,
      dlImageUrl: dlUrls,
      profilePhotoUrl: profileUrls,

      // Compliance
      insuranceNumber: fullDriver.insuranceNumber || "",
      insuranceStatus: fullDriver.insuranceStatus || DocStatus.MISSING,
      insuranceUrls: insUrls,
      pucNumber: fullDriver.pucNumber || "",
      pucStatus: fullDriver.pucStatus || DocStatus.MISSING,
      pucUrls: pucUrls,
      statePermitNumber: fullDriver.statePermitNumber || "",
      statePermitStatus: fullDriver.statePermitStatus || DocStatus.MISSING,
      statePermitUrls: permUrls,
      aitpNumber: fullDriver.aitpNumber || "",
      aitpStatus: fullDriver.aitpStatus || DocStatus.MISSING,
      aitpUrls: aitpUrls,
    });

    if(type === 'MANDATORY') setVerifiedModalOpen(true);
    else setComplianceModalOpen(true);
  };

  const handleApproveSubmit = async () => {
    if (verifiedForm.id == null) return;
    try {
      // We manually sync the booleans for the backend logic
      await approveDriver(verifiedForm.id, {
        // Send Enum Statuses
        rcStatus: verifiedForm.rcStatus,
        dlStatus: verifiedForm.dlStatus,

        // Derive Booleans (Backend requires these for final approval logic)
        isRCVerified: verifiedForm.rcStatus === DocStatus.VERIFIED,
        isDLVerified: verifiedForm.dlStatus === DocStatus.VERIFIED,

        isProfilePhotoVerified: verifiedForm.isProfilePhotoVerified,
        isAvailable: verifiedForm.isAvailable,
      });
      toast.success("Driver approved");
      setVerifiedModalOpen(false);
      refreshDrivers();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to approve driver"));
    }
  };

  const handleComplianceSubmit = async () => {
    if (verifiedForm.id == null) return;
    try {
      await approveDriver(verifiedForm.id, {
        // Save Statuses
        insuranceStatus: verifiedForm.insuranceStatus,
        pucStatus: verifiedForm.pucStatus,
        statePermitStatus: verifiedForm.statePermitStatus,
        aitpStatus: verifiedForm.aitpStatus,
        // Save Editable Numbers
        insuranceNumber: verifiedForm.insuranceNumber,
        pucNumber: verifiedForm.pucNumber,
        statePermitNumber: verifiedForm.statePermitNumber,
        aitpNumber: verifiedForm.aitpNumber,
      });
      toast.success("Compliance documents saved");
      setComplianceModalOpen(false);
      refreshDrivers();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to save compliance documents"));
    }
  };

  const handleSuspend = async (id: number) => {
    const ok = await confirm({
      title: "Mark driver unavailable",
      message: "This driver will stop receiving ride requests. Continue?",
      confirmText: "Mark Unavailable",
    });
    if (!ok) return;
    setLoading(true);
    try {
      await suspendDriver(id);
      toast.success("Driver marked unavailable");
      refreshDrivers();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to update driver"));
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (id: number) => {
    setLoading(true);
    try {
      await activateDriver(id);
      toast.success("Driver marked available");
      refreshDrivers();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to update driver"));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Release a driver from the agent who claimed them. An approved driver gets a second, sharper
   * prompt: clearing the approval un-approves someone who may be driving right now, so it is a
   * separate deliberate answer rather than a checkbox buried in the first dialog.
   */
  const handleUnclaim = async (driver: any) => {
    const ok = await confirm({
      title: "Release driver from agent",
      message: "This clears who enrolled them, so another agent can claim them. Continue?",
      confirmText: "Release",
    });
    if (!ok) return;

    let resetApproval = false;
    if (driver?.approvedAt) {
      resetApproval = await confirm({
        title: "Also clear the approval?",
        message: "This driver is approved. Clearing it un-approves them AND sends RC, DL and photo back to Pending for review — they return to the \"Ready to approve\" list. Only do this if the approval itself was a mistake. Choose Cancel to release them but keep the approval.",
        destructive: true,
        confirmText: "Clear approval too",
      });
    }

    setLoading(true);
    try {
      await unclaimDriver(driver.id, resetApproval);
      toast.success(resetApproval ? "Driver released and approval cleared" : "Driver released from their agent");
      refreshDrivers();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to release driver"));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({
      title: "Delete driver",
      message: "This permanently deletes the driver and cannot be undone. Continue?",
      destructive: true,
      confirmText: "Delete",
    });
    if (!ok) return;
    setLoading(true);
    try {
      await deleteDriver(id);
      toast.success("Driver deleted");
      refreshDrivers();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to delete driver"));
    } finally {
      setLoading(false);
    }
  };

  const handleShowRides = (row: any) => {
    if (!row?.userUid) return;
    const query = `?driverUid=${encodeURIComponent(row.userUid)}`;
    window.location.href = `/rides${query}`;
  };

  const handleShowWalletDetails = (row: any) => {
    if (!row?.userUid) return;
    const query = `?driverUid=${encodeURIComponent(row.userUid)}`;
    window.location.href = `/wallet-transactions${query}`;
  };

  // Search is applied server-side; here we only sort the already-loaded rows
  // (the column headers act on the loaded page set).
  const driverSortVal = (d: any, key: string) => {
    switch (key) {
      case 'appType': return d.user?.appType;
      case 'currentCity': return d.user?.currentCity;
      case 'phone': return d.user?.phone;
      // Sorts NULL (old app) below every real build rather than scattering it.
      case 'appVersion': return d.user?.driverAppVersion ?? -1;
      case 'enrolledBy': return d.enrolledBy?.firstName;
      case 'approvedBy': return d.approvedBy?.firstName;
      default: return (d as any)[key];
    }
  };
  const paginatedDrivers = useMemo(() => {
    const rows = driversList.rows;
    if (!orderBy) return rows;
    return [...rows].sort((a, b) => {
      const va = driverSortVal(a, orderBy);
      const vb = driverSortVal(b, orderBy);
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'string' && typeof vb === 'string') {
        return order === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      if (va < vb) return order === 'asc' ? -1 : 1;
      if (va > vb) return order === 'asc' ? 1 : -1;
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driversList.rows, order, orderBy]);

  return (
      <StyledPaper sx={{ maxWidth: "98vw", width: "100%", margin: '0 auto', my: 2, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', overflow: 'hidden' }}>
        <DriverFiltersToolbar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            availabilityFilter={availabilityFilter}
            setAvailabilityFilter={setAvailabilityFilter}
            approvalFilter={approvalFilter}
            setApprovalFilter={setApprovalFilter}
            appVersionFilter={appVersionFilter}
            setAppVersionFilter={setAppVersionFilter}
            appVersionOptions={appVersionOptions}
        />
        <DriverTable
            rows={paginatedDrivers}
            loading={loading || driversList.isLoading}
            order={order}
            orderBy={orderBy}
            onSort={handleSort}
            onRowClick={handleRowClick}
            onContextMenu={handleContextMenu}
            appVersionGate={appVersionOptions.gate}
        />
        <LoadMoreBar
            count={paginatedDrivers.length}
            hasMore={driversList.hasMore}
            loading={driversList.isLoadingMore}
            onLoadMore={driversList.loadMore}
        />

        <DriverQuickViewModal
            open={detailOpen}
            onClose={handleCloseDetail}
            editLoading={editLoading}
            selectedDriver={selectedDriver}
            editData={editData}
            onFieldChange={handleFieldChange}
            onSave={handleSaveEdit}
            canWrite={canWriteDrivers}
            onGoToDetails={(id) => navigate(`/driver/${id}`)}
        />

        <DriverApproveModal
            open={verifiedModalOpen}
            onClose={() => setVerifiedModalOpen(false)}
            form={verifiedForm}
            setForm={setVerifiedForm}
            onApprove={handleApproveSubmit}
        />

        <DriverComplianceModal
            open={complianceModalOpen}
            onClose={() => setComplianceModalOpen(false)}
            form={verifiedForm}
            setForm={setVerifiedForm}
            onSave={handleComplianceSubmit}
        />

        <FancyContextMenu
            actions={actions}
            open={Boolean(menu)}
            onClose={handleCloseMenu}
            anchorPosition={menu}
            title={selectedRow?.name || selectedRow?.userUid || "Row Actions"}
        />
      </StyledPaper>
  );
};

export default DriverPage;
